import axios, { AxiosError, type AxiosInstance } from 'axios'

// HTTP-клиент Backupex.
//   - baseURL — /api/v1 (через nginx-прокси или vite proxy).
//   - withCredentials — сессионная cookie.
//   - CSRF — читаем cookie `bx_csrf` и кладём в header X-CSRF-Token на mutating.
//   - 401 — однократный пинок к /login (через global event), без бесконечных рекурсий.
//   - Все остальные не-2xx — парсятся как problem-json и err.message
//     перезаписывается человекочитаемым `detail` (см. apiErrorMessage).

const BASE = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_BASE ?? '/api/v1'

export const http: AxiosInstance = axios.create({
  baseURL: BASE,
  withCredentials: true,
  timeout: 30_000,
})

// BackupexProblem — форма ответа из server/internal/httpapi/problem.go.
// Совпадает 1:1 с тем, что сервер кладёт в тело при не-2xx.
export interface BackupexProblem {
  type?: string
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string>
}

// BackupexError — наш расширенный AxiosError: к стандартным полям добавлен
// разобранный problem-json и предсобранный человекочитаемый message.
export interface BackupexError extends AxiosError {
  bxProblem?: BackupexProblem
}

http.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase()
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('bx_csrf')
    if (csrf) {
      config.headers = config.headers || {}
      ;(config.headers as Record<string, string>)['X-CSRF-Token'] = csrf
    }
  }
  return config
})

http.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      // Триггерим глобальный event — слушает router/auth-guard.
      window.dispatchEvent(new CustomEvent('bx:unauthorized'))
    }
    decorateError(err as BackupexError)
    return Promise.reject(err)
  },
)

// decorateError — парсит response.data как problem-json и переписывает err.message
// так, чтобы msg.error(err.message) показывал понятный текст пользователю.
function decorateError(err: BackupexError): void {
  const status = err.response?.status
  const data = err.response?.data as unknown
  // Network / CORS — нет response.
  if (!status) {
    err.message = 'Сетевая ошибка: сервер недоступен'
    return
  }
  if (status === 401) {
    err.message = 'Сессия истекла, нужно войти заново'
    return
  }
  if (status === 403) {
    err.message = 'Недостаточно прав для этой операции'
    return
  }
  // problem-json: разбираем title/detail/errors.
  if (isProblem(data)) {
    err.bxProblem = data
    err.message = formatProblem(data, status)
    return
  }
  // Не problem-json — оставляем generic axios message + статус.
  err.message = `HTTP ${status}: ${err.message}`
}

function isProblem(d: unknown): d is BackupexProblem {
  if (!d || typeof d !== 'object') return false
  const o = d as Record<string, unknown>
  return typeof o.type === 'string' || typeof o.title === 'string' || typeof o.detail === 'string'
}

function formatProblem(p: BackupexProblem, status: number): string {
  // Приоритет: detail → title → "HTTP N". field-level errors добавляем хвостом.
  const parts: string[] = []
  if (p.detail) parts.push(p.detail)
  else if (p.title) parts.push(p.title)
  else parts.push(`HTTP ${status}`)

  if (p.errors && Object.keys(p.errors).length > 0) {
    const fields = Object.entries(p.errors)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ')
    parts.push(`(${fields})`)
  }
  return parts.join(' ')
}

// apiErrorMessage — public-helper для использования в catch'ах вместо
// чтения `e.message` напрямую (плюс типобезопасный fallback).
export function apiErrorMessage(e: unknown, fallback = 'Произошла ошибка'): string {
  if (!e) return fallback
  if (typeof e === 'string') return e
  const ae = e as Partial<BackupexError>
  if (ae.message) return ae.message
  return fallback
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

// PrimeCSRF — лёгкий GET, чтобы сервер выставил cookie до первого POST/PATCH.
export async function primeCSRF(): Promise<void> {
  try {
    await http.get('/me', { validateStatus: () => true })
  } catch {
    /* ignore */
  }
}
